import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  Modal,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
  Spin,
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency, toApiNumber } from "../../../utils/formatters";
import { fetchSystemConfig, readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { parseLocalizedName } from "../../../utils/localization";
import PermissionGuard from "../../../shared/ui/PermissionGuard";
import {
  PurchaseOrderPrintSheet,
  buildPrintModelFromApiPo,
} from "../components/PurchaseOrderPrintSheet";
import "../../../shared/styles/ListPages.css";
import "./PurchaseOrdersList.css";

const { Title, Text } = Typography;

const statusColors = {
  draft: "default",
  ordered: "processing",
  received: "success",
  cancelled: "error",
  partial: "processing",
  paid: "success",
};

const PurchaseOrdersList = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());
  const [poDetailCache, setPoDetailCache] = useState({});
  const [printModel, setPrintModel] = useState(null);
  const [printingRowId, setPrintingRowId] = useState(null);

  const mainCode = systemConfig?.currency_code || "IQD";
  const secondaryCode = systemConfig?.secondary_currency_code || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = await fetchSystemConfig();
      setSystemConfig(cfg);
      const res = await apiClient.get("/purchase-orders", { params: { page, per_page: 20 } });
      const payload = res.data;
      setRows(payload?.data ?? []);
      setTotal(payload?.total ?? 0);
    } catch (e) {
      message.error(e?.response?.data?.message || t("purchaseOrdersLoadFailed", "Failed to load orders."));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!printModel) return undefined;
    let cancelled = false;
    const runPrint = () => {
      if (!cancelled) window.print();
    };
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(runPrint);
    });
    const onAfterPrint = () => setPrintModel(null);
    window.addEventListener("afterprint", onAfterPrint);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(id);
      window.removeEventListener("afterprint", onAfterPrint);
    };
  }, [printModel]);

  const handlePrintRow = useCallback(
    async (record) => {
      setPrintingRowId(record.id);
      try {
        let po = poDetailCache[record.id]?.po;
        if (!po) {
          const res = await apiClient.get(`/purchase-orders/${record.id}`);
          po = res.data;
          setPoDetailCache((prev) => ({
            ...prev,
            [record.id]: { loading: false, po: res.data },
          }));
        }
        const items = Array.isArray(po.items) ? po.items : [];
        if (!items.length) {
          message.warning(t("purchaseOrdersPrintNoLines", "No product lines; printing not available."));
          return;
        }
        const model = buildPrintModelFromApiPo(po, systemConfig, i18n);
        setPrintModel(model);
      } catch (e) {
        message.error(e?.response?.data?.message || t("purchaseOrdersLoadFailed", "Failed to load orders."));
      } finally {
        setPrintingRowId(null);
      }
    },
    [i18n, poDetailCache, systemConfig, t]
  );

  const loadPoDetail = useCallback(
    async (id) => {
      setPoDetailCache((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || {}), loading: true, error: false },
      }));
      try {
        const res = await apiClient.get(`/purchase-orders/${id}`);
        setPoDetailCache((prev) => ({
          ...prev,
          [id]: { loading: false, po: res.data },
        }));
      } catch {
        setPoDetailCache((prev) => ({
          ...prev,
          [id]: { loading: false, error: true },
        }));
      }
    },
    []
  );

  const handleDelete = useCallback(
    (record) => {
      Modal.confirm({
        title: t("purchaseOrdersDeleteConfirm", "Delete this order? Vendor balance will change."),
        okType: "danger",
        onOk: async () => {
          try {
            await apiClient.delete(`/purchase-orders/${record.id}`);
            message.success(t("purchaseOrdersDeleted", "Order deleted."));
            setPoDetailCache((prev) => {
              const next = { ...prev };
              delete next[record.id];
              return next;
            });
            load();
          } catch (e) {
            message.error(e?.response?.data?.message || t("purchaseOrdersDeleteFailed", "Delete failed."));
          }
        },
      });
    },
    [load, t]
  );

  const columns = useMemo(
    () => [
      { title: t("id", "ID"), dataIndex: "id", key: "id", width: 72 },
      {
        title: t("purchaseOrdersColVendor", "Vendor"),
        key: "vendor",
        render: (_, r) => r.vendor?.name ?? "—",
      },
      {
        title: t("purchaseOrdersColReference", "Reference"),
        dataIndex: "reference",
        key: "reference",
        ellipsis: true,
        render: (v) => v || "—",
      },
      {
        title: t("purchaseOrdersColLines", "Lines"),
        dataIndex: "items_count",
        key: "items_count",
        width: 90,
      },
      {
        title: t("purchaseOrdersColTotal", "Total"),
        dataIndex: "total_price",
        key: "total_price",
        render: (v) => formatCurrency(v, mainCode),
      },
      {
        title: t("purchaseOrdersColPaid", "Paid"),
        dataIndex: "amount_paid",
        key: "amount_paid",
        render: (v) => formatCurrency(v, mainCode),
      },
      {
        title: t("purchaseOrdersColOutstanding", "Outstanding"),
        key: "out",
        render: (_, r) => {
          if (r.status === "cancelled" || r.status === "draft") return "—";
          const o = Number(r.total_price) - Number(r.amount_paid);
          return formatCurrency(Math.max(0, o), mainCode);
        },
      },
      {
        title: t("purchaseOrdersColStatus", "Status"),
        dataIndex: "status",
        key: "status",
        render: (s) => (
          <Tag color={statusColors[s] || "default"}>{t(`poStatus_${s}`, s)}</Tag>
        ),
      },
      {
        title: t("purchaseOrdersColActions", ""),
        key: "actions",
        width: 168,
        fixed: "right",
        render: (_, record) => (
          <Space className="po-list-actions" size={2} wrap={false}>
            <Tooltip title={t("print", "Print")}>
              <Button
                type="link"
                size="small"
                icon={<PrinterOutlined />}
                aria-label={t("print", "Print")}
                loading={printingRowId === record.id}
                onClick={() => handlePrintRow(record)}
              />
            </Tooltip>
            <Tooltip title={t("view", "View")}>
              <Link to={`/purchase-orders/${record.id}/edit`}>
                <Button type="link" size="small" icon={<EyeOutlined />} aria-label={t("view", "View")} />
              </Link>
            </Tooltip>
            <Tooltip title={t("edit", "Edit")}>
              <Link to={`/purchase-orders/${record.id}/edit`}>
                <Button type="link" size="small" icon={<EditOutlined />} aria-label={t("edit", "Edit")} />
              </Link>
            </Tooltip>
            <Tooltip title={t("delete", "Delete")}>
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                aria-label={t("delete", "Delete")}
                onClick={() => handleDelete(record)}
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [handleDelete, handlePrintRow, mainCode, printingRowId, t]
  );

  const expandedRowRender = (record) => {
    const entry = poDetailCache[record.id];
    if (!entry || entry.loading) {
      return (
        <div style={{ padding: "16px 24px 24px 48px" }}>
          <Spin />
        </div>
      );
    }
    if (entry.error) {
      return (
        <div style={{ padding: "16px 24px 24px 48px" }}>
          <Text type="danger">{t("purchaseOrdersLoadFailed", "Failed to load orders.")}</Text>
        </div>
      );
    }

    const po = entry.po;
    const items = Array.isArray(po.items) ? po.items : [];
    const vendor = po.vendor || {};
    const outstanding =
      po.status === "cancelled" || po.status === "draft"
        ? 0
        : Math.max(0, toApiNumber(po.total_price, 0) - toApiNumber(po.amount_paid, 0));

    const lineCols = [
      {
        title: t("purchaseOrdersLineProduct", "Product"),
        key: "prod",
        render: (_, row) =>
          parseLocalizedName(
            row.product?.name,
            i18n.language,
            row.product?.sku ? `#${row.product.sku}` : `#${row.product_id}`
          ),
      },
      {
        title: t("purchaseOrdersLineQty", "Qty"),
        dataIndex: "quantity",
        key: "quantity",
        width: 88,
      },
      {
        title: t("purchaseOrdersLinePrice", "Unit Price"),
        dataIndex: "single_price",
        key: "single_price",
        render: (v) => formatCurrency(v, mainCode),
      },
      {
        title: t("purchaseOrdersLineSubtotal", "Line Total"),
        dataIndex: "total_price",
        key: "total_price",
        render: (v) => formatCurrency(v, mainCode),
      },
    ];

    const sec = toApiNumber(po.amount_paid_secondary, 0);
    const mainPaid = toApiNumber(po.amount_paid_main, po.amount_paid ?? 0);
    const showSplit = Boolean(secondaryCode) && (sec > 0.0001 || po.exchange_rate_id != null);

    return (
      <div className="po-list-expanded" style={{ padding: "12px 20px 24px 52px" }}>
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Space wrap split={<Text type="secondary">·</Text>}>
            <Text>
              <strong>{t("purchaseOrdersColVendor", "Vendor")}:</strong> {vendor.name ?? "—"}
            </Text>
            {vendor.phone ? (
              <Text type="secondary">
                {vendor.phone}
              </Text>
            ) : null}
            {po.created_at ? (
              <Text type="secondary">
                {dayjs(po.created_at).format("YYYY-MM-DD HH:mm")}
              </Text>
            ) : null}
            {po.creator?.name ? (
              <Text type="secondary">
                {po.creator.name}
              </Text>
            ) : null}
          </Space>

          <div>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              {t("poCardProductsTitle", "Lines & Products")}
            </Text>
            <Table
              size="small"
              rowKey={(row) => row.id ?? `${row.product_id}-${row.quantity}`}
              columns={lineCols}
              dataSource={items}
              pagination={false}
              locale={{ emptyText: t("poLinesEmpty", "No lines — search for a product and add it.") }}
              scroll={{ x: 520 }}
            />
          </div>

          <div>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              {t("poPaymentSection", "Payment")}
            </Text>
            <Space direction="vertical" size={4}>
              <Text>
                {t("purchaseOrdersColTotal", "Total")}: <strong>{formatCurrency(po.total_price, mainCode)}</strong>
              </Text>
              <Text>
                {t("purchaseOrdersColPaid", "Paid")}: <strong>{formatCurrency(po.amount_paid, mainCode)}</strong>
              </Text>
              <Text>
                {t("purchaseOrdersColOutstanding", "Outstanding")}: <strong>{formatCurrency(outstanding, mainCode)}</strong>
              </Text>
              {showSplit ? (
                <>
                  <Text type="secondary">
                    {t("poAmountPaidMain", "Amount Paid ({{code}})", { code: mainCode })}: {formatCurrency(mainPaid, mainCode)}
                  </Text>
                  <Text type="secondary">
                    {t("poAmountPaidSecondary", "Amount Paid in secondary currency ({{code}})", { code: secondaryCode })}: {formatCurrency(sec, secondaryCode)}
                  </Text>
                  {po.exchange_rate?.main_per_secondary ? (
                    <Text type="secondary">
                      {t("exchangeRate", "Exchange rate")}: 1 {mainCode} = {po.exchange_rate.main_per_secondary} {secondaryCode}
                    </Text>
                  ) : null}
                </>
              ) : null}
            </Space>
          </div>
        </Space>
      </div>
    );
  };

  return (
    <div className="list-page po-list-page">
      <div className="no-print-po">
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Space
            wrap
            className="po-list-header-row"
            style={{ justifyContent: "space-between", width: "100%", alignItems: "center" }}
          >
            <Title level={4} className="po-list-title">
              {t("purchaseOrdersTitle", "Purchase Orders")}
            </Title>
            <Space wrap align="center" className="po-list-toolbar">
              <PermissionGuard permission="vendors.index" hide>
                <Link to="/vendors">{t("purchaseOrdersManageVendors", "Manage Vendors")}</Link>
              </PermissionGuard>
              <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
                {t("currencyTotalsRefresh", "Refresh")}
              </Button>
              <Link to="/purchase-orders/new">
                <Button type="primary" icon={<PlusOutlined />}>
                  {t("purchaseOrdersAdd", "New Order")}
                </Button>
              </Link>
            </Space>
          </Space>

          <Card styles={{ body: { padding: 0 } }} size="small">
            <Table
              rowKey="id"
              size="small"
              loading={loading}
              columns={columns}
              dataSource={rows}
              pagination={{
                current: page,
                total,
                pageSize: 20,
                showSizeChanger: false,
                onChange: (p) => setPage(p),
              }}
              scroll={{ x: true }}
              expandable={{
                expandedRowRender,
                onExpand: (expanded, record) => {
                  if (!expanded) return;
                  const cur = poDetailCache[record.id];
                  if (cur?.po) return;
                  if (cur?.loading) return;
                  loadPoDetail(record.id);
                },
              }}
            />
          </Card>
        </Space>
      </div>

      {printModel ? (
        <PurchaseOrderPrintSheet systemConfig={systemConfig} t={t} {...printModel} />
      ) : null}
    </div>
  );
};

export default PurchaseOrdersList;
